// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson

export type YearMap = Record<string, number | null>;

export interface RegionRow { name: string; state: string | null; values: YearMap }
export interface MapData {
  years: number[]; latestYear: number; baseYear: number;
  regions: RegionRow[];
  offMap: { name: string; values: YearMap }[];
}
export interface StatesData { years: number[]; rows: { name: string; values: YearMap }[] }
export interface ClassRow { label: string; values: YearMap }
export interface ClassificationData {
  years: number[]; men: ClassRow[]; women: ClassRow[]; total: ClassRow[];
  totalsByYear: Record<string, number>; genderX: number;
}
export interface Agency {
  name: string; slug: string; isSub: boolean; portfolio: string | null;
  headcount: number | null; female: number | null; indigenous: number | null;
  disability: number | null; cald: number | null; nonOngoing: number | null;
  partTime: number | null; atApsLevel: number | null; meanAge: number | null;
  medianLos: number | null; oneAgencyOnly: number | null; bachelorPlus: number | null;
  exitRate: number | null; separationRate: number | null;
}
export interface FlowRow {
  name: string; slug: string; isSub: boolean; portfolio: string | null;
  within: number | null; from: number | null; to: number | null;
  net: number | null; total: number | null;
  fromSuppressed: boolean; toSuppressed: boolean;
}
export interface Fingerprint {
  name: string; slug: string; isSub: boolean; portfolio: string | null;
  grades: { g: string; v: number | null; s: boolean }[];
}
export interface AgenciesData {
  agencies: Agency[]; flows: FlowRow[];
  jobFamily: { columns: string[]; rows: { label: string; cells: { key: string; value: number | null; suppressed: boolean }[] }[] };
  fingerprints: Fingerprint[]; gradeOrder: string[];
}
export interface MovementData {
  columns: string[];
  rows: { label: string; cells: { key: string; value: number | null; suppressed: boolean }[] }[];
}
export interface IntakeSeries { years: number[]; rows: ClassRow[] }
export interface IntakeData { engagements: IntakeSeries; separations: IntakeSeries }
export interface DiversityData { years: number[]; rows: { label: string; values: YearMap }[]; age: { label: string; values: YearMap }[] }
export interface OriginsData { countries: { label: string; value: number }[]; languages: { label: string; value: number }[] }
export interface Meta {
  asAt: string; release: string; licence: string; sourceUrl: string; generated: string;
  total: number; years: number[];
  releases: { title: string; asAt: string }[];
  gates: { name: string; ok: boolean; detail: string }[];
  gatesPassed: number; gatesTotal: number;
}

export interface Bundle {
  meta: Meta; map: MapData; states: StatesData; classification: ClassificationData;
  agencies: AgenciesData; movement: MovementData; intake: IntakeData;
  diversity: DiversityData; origins: OriginsData;
  geo: GeoJSON.FeatureCollection;
}

async function getJSON<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(path, { signal });
  if (!res.ok) throw new Error(`${path} returned HTTP ${res.status}`);
  return (await res.json()) as T;
}

/**
 * Data files are versioned by the release's as-at date, and meta.json is always
 * revalidated, so a returning visitor on a cached bundle can never run new code
 * against stale numbers without the mismatch being visible.
 */
export async function loadAll(signal?: AbortSignal): Promise<Bundle> {
  const [meta, map, states, classification, agencies, movement, intake, diversity, origins, geo] =
    await Promise.all([
      getJSON<Meta>('data/meta.json', signal),
      getJSON<MapData>('data/map.json', signal),
      getJSON<StatesData>('data/states.json', signal),
      getJSON<ClassificationData>('data/classification.json', signal),
      getJSON<AgenciesData>('data/agencies.json', signal),
      getJSON<MovementData>('data/movement.json', signal),
      getJSON<IntakeData>('data/intake.json', signal),
      getJSON<DiversityData>('data/diversity.json', signal),
      getJSON<OriginsData>('data/origins.json', signal),
      getJSON<GeoJSON.FeatureCollection>('data/sa4.geojson', signal),
    ]);
  return { meta, map, states, classification, agencies, movement, intake, diversity, origins, geo };
}

/** APS classifications in rank order, junior first. */
export const GRADE_ORDER = [
  'Trainee', 'Graduate', 'APS 1', 'APS 2', 'APS 3', 'APS 4', 'APS 5', 'APS 6',
  'EL 1', 'EL 2', 'SES 1', 'SES 2', 'SES 3',
];

export function gradeRank(g: string): number {
  const i = GRADE_ORDER.indexOf(g);
  return i < 0 ? GRADE_ORDER.length : i;
}

/** Full names for the state codes APSED uses. */
export const STATE_NAMES: Record<string, string> = {
  ACT: 'Australian Capital Territory', NSW: 'New South Wales', VIC: 'Victoria',
  QLD: 'Queensland', SA: 'South Australia', WA: 'Western Australia',
  TAS: 'Tasmania', NT: 'Northern Territory', "O'seas": 'Overseas', OS: 'Overseas',
};

export function stateName(code: string): string {
  return STATE_NAMES[code] ?? code;
}

/** One stable colour per state, used identically in every view. */
export const STATE_COLOURS: Record<string, string> = {
  ACT: '#12385c', VIC: '#2d86ab', NSW: '#3d9fb5', QLD: '#7fd1c8',
  SA: '#b26a00', WA: '#a33b52', TAS: '#5b7c99', NT: '#8a6d3b', "O'seas": '#7b8ea1',
};

export function stateColour(code: string | null): string {
  return (code && STATE_COLOURS[code]) || '#7b8ea1';
}
